import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, AlertTriangle, X, Check, Building2, Heart, Users, Award, TrendingUp } from "lucide-react";
import Seo from "../lib/seo";
import { STATS } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import AnimatedCounter from "../components/AnimatedCounter";
import PetitionCTA from "../components/PetitionCTA";

const CAPITOL_IMG = "https://images.pexels.com/photos/11482790/pexels-photo-11482790.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LegislationPage() {
    return (
        <>
            <Seo
                title="The Legislation — Hotel Cardiac Emergency Preparedness Act"
                description="JC Law is the Hotel Cardiac Emergency Preparedness Act. Learn the requirements, the statistics behind the mission, and what JC Law will do to save lives."
                path="/legislation"
                image={CAPITOL_IMG}
            />

            <PageHeader
                overline="The Legislation"
                title="The background behind JC Law."
                subtitle="JC Law is the Hotel Cardiac Emergency Preparedness Act — ensuring hotels and lodging facilities are prepared to respond when every second matters most."
                image={CAPITOL_IMG}
            />

            {/* THE REALITY — Stats */}
            <section className="bg-editorial py-24 md:py-32" data-testid="legislation-reality">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <Reveal className="max-w-3xl mb-14">
                        <p className="overline mb-5 text-coral">The Reality</p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy-900 leading-[1.05]">
                            In a cardiac emergency, minutes matter. Immediate action can mean the difference between life and death.
                        </h2>
                    </Reveal>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-editorial-border">
                        {STATS.map((s, i) => (
                            <Reveal key={i} delay={i * 0.08} className="bg-editorial p-8 md:p-10">
                                <div className="font-serif text-5xl md:text-6xl text-coral tracking-tight">
                                    <AnimatedCounter value={s.value} prefix={s.prefix || ""} suffix={s.suffix} />
                                </div>
                                <div className="mt-4 text-sm text-navy-800/80 leading-snug">{s.label}</div>
                                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-navy-700/50">{s.source}</div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Infographic 1: The Background & Reality */}
            <section className="bg-navy-950 py-16 md:py-20" data-testid="legislation-infographic-1">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
                    <Reveal>
                        <p className="overline mb-4 text-coral text-center">Infographic · At A Glance</p>
                        <div className="relative overflow-hidden border border-white/10 shadow-2xl group">
                            <img
                                src="/images/legislation-1.png"
                                alt="The Background Behind JC Law — cardiac arrest statistics: 350,000+ OHCA/year, 90% drop, 2-3x AED survival, 20,000+ hotels lacking AEDs, 7 of 10 in homes/public places"
                                className="w-full h-auto block transition-transform duration-[2000ms] group-hover:scale-[1.02]"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* THE PROBLEM */}
            <section className="bg-navy-900 text-editorial py-24 md:py-32 relative overflow-hidden" data-testid="legislation-problem">
                <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                    <Reveal className="lg:col-span-5">
                        <p className="overline mb-5">The Problem</p>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-6">
                            Most hotels and lodging facilities are not equipped to respond.
                        </h2>
                        <p className="text-editorial/75 text-lg leading-relaxed">
                            With every passing minute without CPR and defibrillation, the chance of survival drops dramatically. Every minute counts.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-7 space-y-4">
                        {[
                            "AEDs are missing or inaccessible",
                            "Staff are not trained in CPR",
                            "No clear emergency response protocols",
                            "Minutes are lost — lives are at risk",
                        ].map((item, i) => (
                            <div key={i} className="flex items-start gap-5 p-6 border border-white/10 bg-white/5">
                                <X className="w-6 h-6 text-coral flex-shrink-0 mt-0.5" />
                                <p className="text-lg text-editorial/90">{item}</p>
                            </div>
                        ))}
                    </Reveal>
                </div>
            </section>

            {/* THE SOLUTION */}
            <section className="bg-editorial py-24 md:py-32" data-testid="legislation-solution">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                    <Reveal className="lg:col-span-5">
                        <p className="overline mb-5 text-coral">The Solution</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy-900 leading-[1.05] mb-6">
                            JC Law establishes statewide preparedness standards.
                        </h2>
                        <p className="text-navy-800/75 text-lg leading-relaxed">
                            Clear, reasonable requirements that protect guests and empower staff to act in the critical first minutes of a cardiac emergency.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-7 space-y-4">
                        {[
                            "Require accessible AEDs on-site",
                            "Ensure CPR-trained staff on every shift",
                            "Implement clear emergency response protocols",
                            "Require regular equipment checks and maintenance",
                            "Provide ongoing training and certification",
                        ].map((item, i) => (
                            <div key={i} className="flex items-start gap-5 p-6 bg-white border border-editorial-border">
                                <Check className="w-6 h-6 text-navy-900 flex-shrink-0 mt-0.5" />
                                <p className="text-lg text-navy-800">{item}</p>
                            </div>
                        ))}
                    </Reveal>
                </div>
            </section>

            {/* Infographic 2: The Solution & Cost vs Inaction */}
            <section className="bg-navy-950 py-16 md:py-20" data-testid="legislation-infographic-2">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
                    <Reveal>
                        <p className="overline mb-4 text-coral text-center">Infographic · The Solution</p>
                        <div className="relative overflow-hidden border border-white/10 shadow-2xl group">
                            <img
                                src="/images/legislation-2.png"
                                alt="JC Law — The Solution and Cost of Preparedness vs. Cost of Inaction. AED $1,000–$2,000, CPR Training $20–$50/staff, vs. Lives Lost, Legal Liability, Reputation Damage. What JC Law will do: Save Lives, Protect Communities, Set a New Standard, Reduce Liability, Build Confidence."
                                className="w-full h-auto block transition-transform duration-[2000ms] group-hover:scale-[1.02]"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* COST vs INACTION */}
            <section className="bg-editorial-muted py-24" data-testid="legislation-cost">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <Reveal className="max-w-3xl mb-12">
                        <p className="overline mb-5 text-coral">Cost of Preparedness vs. Inaction</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy-900 leading-tight">A small investment that saves lives.</h2>
                    </Reveal>
                    <div className="grid md:grid-cols-2 gap-6">
                        <Reveal>
                            <div className="bg-white p-10 border-l-4 border-navy-900 h-full">
                                <div className="flex items-center gap-3 mb-6">
                                    <ShieldCheck className="w-7 h-7 text-navy-900" />
                                    <h3 className="font-serif text-2xl text-navy-900">Cost of Preparedness</h3>
                                </div>
                                <ul className="space-y-4 text-navy-800/85">
                                    <li><strong className="font-semibold">AED Device:</strong> $1,000 – $2,000 one-time cost</li>
                                    <li><strong className="font-semibold">CPR Training:</strong> $20 – $50 per staff member</li>
                                    <li><strong className="font-semibold">Maintenance:</strong> Minimal annual cost</li>
                                </ul>
                                <p className="mt-8 pt-6 border-t border-editorial-border text-2xl font-serif text-navy-900">Priceless: <span className="italic">A life saved.</span></p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <div className="bg-navy-900 text-editorial p-10 border-l-4 border-coral h-full">
                                <div className="flex items-center gap-3 mb-6">
                                    <AlertTriangle className="w-7 h-7 text-coral" />
                                    <h3 className="font-serif text-2xl">Cost of Inaction</h3>
                                </div>
                                <ul className="space-y-4 text-editorial/85">
                                    <li><strong className="font-semibold">Lives Lost:</strong> Irreversible impact on families and communities</li>
                                    <li><strong className="font-semibold">Legal Liability:</strong> Potential lawsuits, fines, and legal consequences</li>
                                    <li><strong className="font-semibold">Reputation Damage:</strong> Loss of trust, negative publicity, long-term financial impact</li>
                                </ul>
                                <p className="mt-8 pt-6 border-t border-white/10 text-2xl font-serif text-coral">Cost: <span className="italic">A life lost.</span></p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* WHAT JC LAW WILL DO */}
            <section className="bg-editorial py-24 md:py-32" data-testid="legislation-impact">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <Reveal className="max-w-3xl mb-12">
                        <p className="overline mb-5 text-coral">What JC Law Will Do</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy-900 leading-tight">Together, we can make every second count.</h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-editorial-border">
                        {[
                            { icon: Heart, title: "Save Lives" },
                            { icon: Building2, title: "Protect Communities" },
                            { icon: Award, title: "Set A New Standard" },
                            { icon: ShieldCheck, title: "Reduce Liability" },
                            { icon: TrendingUp, title: "Build Confidence" },
                        ].map((c, i) => (
                            <Reveal key={i} delay={i * 0.06} className="bg-editorial p-8 lg:p-10">
                                <c.icon className="w-8 h-8 text-coral mb-5" />
                                <h3 className="font-serif text-2xl text-navy-900 leading-tight">{c.title}</h3>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.3} className="mt-14 max-w-2xl">
                        <p className="text-lg text-navy-800/80 leading-relaxed">
                            Support JC Law and help create a safer future for guests, staff, and communities across South Carolina — and beyond.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link to="/get-involved" className="px-7 py-3.5 bg-navy-900 text-editorial hover:bg-navy-800 transition-colors flex items-center gap-2">
                                Get Involved <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/contact" className="px-7 py-3.5 border border-navy-900 hover:bg-navy-900 hover:text-editorial transition-colors">
                                Contact Legislators
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <PetitionCTA />
        </>
    );
}
